import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CardComponent,
  deleteConfirmationDialog,
} from '@sportello-balneari/shared-components';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { of } from 'rxjs';
import { PremessaApiService } from '../../../../api/premessa-api.service';
import { AnswerPermissionsService } from '../../../../services/compiling/state/answer-permissions.service';
import { EVALUATION_CAPABILITIES } from '../../../../services/evaluation-capabilities';

/** Filename used for the browser download of the compiled offerta tecnica document. */
const TEMPLATE_DOWNLOAD_FILENAME = 'Offerta_Tecnica.docx';

/** Shown when the upload fails without an actionable message from the backend (network error, 401, 500...). */
const UPLOAD_GENERIC_ERROR_MESSAGE = 'Caricamento della premessa fallito. Riprovare.';

@Component({
  selector: 'lib-premessa-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
  templateUrl: './premessa-panel.component.html',
  imports: [
    CardComponent,
    ButtonModule,
    ConfirmDialogModule,
    Toast,
    DatePipe,
    TooltipModule,
  ],
})
export class PremessaPanelComponent {
  private readonly api = inject(PremessaApiService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly tenderFormAnswerId = input.required<string>();

  protected readonly capabilities = inject(EVALUATION_CAPABILITIES);
  protected readonly permissions = inject(AnswerPermissionsService);
  protected readonly downloading = signal(false);
  protected readonly uploading = signal(false);
  protected readonly deleting = signal(false);

  /** Current premessa status for the tender form answer; reloaded after upload/delete. */
  protected readonly premessaResource = rxResource({
    params: () => ({ tenderFormAnswerId: this.tenderFormAnswerId() }),
    stream: ({ params }) => {
      if (!params.tenderFormAnswerId) return of(null);
      return this.api.getPremessa(params.tenderFormAnswerId);
    },
  });

  protected downloadTemplate(): void {
    const id = this.tenderFormAnswerId();

    this.downloading.set(true);
    this.api
      .downloadTemplate(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          this.downloading.set(false);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = TEMPLATE_DOWNLOAD_FILENAME;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: () => {
          this.downloading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile scaricare il documento. Riprovare.',
            life: 5000,
          });
        },
      });
  }

  protected onFilePicked(event: Event, fileInput: HTMLInputElement): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.uploadPremessa(file);
    fileInput.value = '';
  }

  protected confirmDelete(): void {
    deleteConfirmationDialog(this.confirmationService, {
      message:
        'Vuoi eliminare la premessa caricata? Questa azione non può essere annullata.',
      accept: () => this.deletePremessa(),
    });
  }

  private uploadPremessa(file: File): void {
    const id = this.tenderFormAnswerId();

    this.uploading.set(true);
    this.api
      .uploadPremessa(id, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.uploading.set(false);
          this.premessaResource.reload();
          this.messageService.add({
            severity: 'success',
            summary: 'Premessa caricata',
            detail: 'Il documento è stato caricato con successo.',
            life: 3000,
          });
        },
        error: (err: HttpErrorResponse) => {
          this.uploading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: this.extractUploadErrorMessage(err),
            life: 5000,
          });
        },
      });
  }

  /**
   * The backend rejects some uploads (e.g. a docx whose protected offerta tecnica content control was
   * stripped by an editor other than Word) with a 400 whose body is a plain-string, actionable Italian
   * message. Surface it when present; fall back to the generic message for anything else (network error,
   * 401, 500...), where there is nothing useful to show the candidate.
   */
  private extractUploadErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 400 && typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }
    return UPLOAD_GENERIC_ERROR_MESSAGE;
  }

  private deletePremessa(): void {
    const id = this.tenderFormAnswerId();

    this.deleting.set(true);
    this.api
      .deletePremessa(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false);
          this.premessaResource.reload();
        },
        error: () => {
          this.deleting.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Eliminazione della premessa fallita. Riprovare.',
            life: 5000,
          });
        },
      });
  }
}
